/**
 * SkillRow Component
 * Table-like row for displaying a skill with drag handle
 */

import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Edit2, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { useI18n } from '@/i18n';
import type { Skill } from '@/types/skills';

interface SkillRowProps {
  skill: Skill;
  index: number;
  isDragging?: boolean;
  onEdit: (skill: Skill) => void;
  onDelete: (skill: Skill) => void;
  onToggle: (skill: Skill) => void;
  isOverlay?: boolean;
}

export const SkillRow: React.FC<SkillRowProps> = ({
  skill,
  index,
  onEdit,
  onDelete,
  onToggle,
  isOverlay = false,
}) => {
  const { t } = useI18n();
  const [showTranslations, setShowTranslations] = useState(false);
  const translations = skill.translations || {};
  const hasEnTranslation = !!translations.en;
  const translationLocales = Object.keys(translations);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isThisDragging,
  } = useSortable({
    id: skill.id,
    disabled: isOverlay,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={`
          grid grid-cols-[40px_160px_minmax(180px,1fr)_minmax(200px,1.5fr)_80px_100px] gap-4 px-4 py-3 items-center
          bg-white hover:bg-gray-50 transition-colors
          ${isThisDragging ? 'opacity-50 bg-primary-50' : ''}
          ${!skill.enabled ? 'opacity-60' : ''}
        `}
      >
        {/* Drag Handle */}
        <button
          type="button"
          className="cursor-move text-gray-400 hover:text-gray-600 flex items-center justify-center"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-5 h-5" />
        </button>

        {/* Skill ID - show full */}
        <div className="min-w-0">
          <span className="text-sm font-mono text-gray-600" title={skill.skill_id}>
            {skill.skill_id}
          </span>
        </div>

        {/* Name & Description */}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 font-mono">#{index + 1}</span>
            <span className="font-medium text-gray-900 truncate">{skill.name}</span>
            {/* Translation badge */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (translationLocales.length > 0) {
                  setShowTranslations(!showTranslations);
                }
              }}
              className={`
                inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0
                ${hasEnTranslation
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-400'}
              `}
              title={hasEnTranslation ? t('skillsPage.translations') : t('skillsPage.noTranslations')}
            >
              EN {hasEnTranslation ? '\u2713' : '\u2014'}
              {translationLocales.length > 0 && (
                showTranslations
                  ? <ChevronDown className="w-3 h-3" />
                  : <ChevronRight className="w-3 h-3" />
              )}
            </button>
          </div>
          <p className="text-sm text-gray-500 truncate mt-0.5">{skill.description || '-'}</p>
        </div>

        {/* Command */}
        <div className="min-w-0">
          <p className="text-sm text-gray-600 truncate" title={skill.command}>
            {skill.command}
          </p>
        </div>

        {/* Status Toggle */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(skill);
            }}
            className={`
              w-10 h-6 rounded-full transition-colors relative flex-shrink-0
              ${skill.enabled ? 'bg-green-500' : 'bg-gray-300'}
            `}
            title={skill.enabled ? 'Click to disable' : 'Click to enable'}
          >
            <div
              className={`
                w-4 h-4 bg-white rounded-full shadow transform transition-transform absolute top-1
                ${skill.enabled ? 'translate-x-5' : 'translate-x-1'}
              `}
            />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(skill);
            }}
            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Edit skill"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(skill);
            }}
            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Delete skill"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Collapsible Translation Details */}
      {showTranslations && translationLocales.length > 0 && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
          <div className="ml-10 text-xs">
            <span className="font-medium text-gray-500 uppercase tracking-wider">
              {t('skillsPage.translations')}
            </span>
            <table className="mt-1 w-full">
              <thead>
                <tr className="text-left text-gray-400">
                  <th className="pr-4 py-0.5 font-medium">Locale</th>
                  <th className="pr-4 py-0.5 font-medium">Field</th>
                  <th className="py-0.5 font-medium">Translation</th>
                </tr>
              </thead>
              <tbody className="text-gray-600">
                {translationLocales.map((locale) => {
                  const trans = translations[locale];
                  const fields = [
                    { key: 'name', value: trans.name },
                    { key: 'description', value: trans.description },
                    { key: 'command', value: trans.command },
                  ].filter((f) => f.value);
                  return fields.map((field, i) => (
                    <tr key={`${locale}-${field.key}`} className="border-t border-gray-100">
                      {i === 0 ? (
                        <td className="pr-4 py-0.5 font-mono text-gray-500" rowSpan={fields.length}>
                          {locale.toUpperCase()}
                        </td>
                      ) : null}
                      <td className="pr-4 py-0.5 font-mono">{field.key}</td>
                      <td className="py-0.5 truncate max-w-md" title={field.value}>
                        {field.value}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
